import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import { Box, Fade, Menu, MenuItem, type SxProps, type Theme } from '@mui/material';
import { Check, Warning } from '@phosphor-icons/react';
import { ComboBoxOption, ComboBoxApi } from 'components/inputs/types';
import { omit, debounce } from 'lodash';
import React, { useState, useCallback, useMemo } from 'react';
import { palette } from 'styles/theme/palette';
import {
  Avatar,
  AvatarGroup,
  Button,
  Chip,
  ChipProps,
  DebouncedInput,
  Text,
  TextProps,
} from 'components';
import type { Variants } from 'types/emotion.d.ts';
import { useGet } from 'lib/api';
import { parseSearch } from 'components/inputs/ComboBox';

/** Config per size variant; extend when adding new size-specific styles (e.g. gap, borderRadius). */
export type CustomDropDownSizeConfig = {
  listItemHeight: number;
  fontSize?: string | undefined;
  /** Vertical and horizontal padding for option rows (e.g. '4px 10px') */
  optionPadding?: string;
  /** Padding for loading / no-options row */
  emptyStatePadding?: string;
};

export const CUSTOM_DROPDOWN_SIZE_CONFIG: Record<'sm' | 'md', CustomDropDownSizeConfig> = {
  sm: {
    listItemHeight: 36,
    fontSize: '14px',
    optionPadding: '4px 10px',
    emptyStatePadding: '8px',
  },
  md: {
    listItemHeight: 40,
    optionPadding: '6px 10px',
    emptyStatePadding: '8px',
  },
};

export interface CustomDropDownProps {
  options: ComboBoxOption[];
  value?: Partial<ComboBoxOption>[];
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
  menuWidth?: number;
  /** When true, only one option can be selected; selecting replaces current value and closes menu */
  singleSelect?: boolean;
  enableSearch?: boolean;
  renderOption?: (option?: ComboBoxOption) => React.ReactNode;
  optionsChipRender?: (option: ComboBoxOption) => React.ReactNode;
  isInvalidOption?: (option: ComboBoxOption) => boolean;
  /** Non-selectable option (disabled MenuItem, no warning icon). Unlike `isInvalidOption`, no warning styling. */
  isDisabledOption?: (option: ComboBoxOption) => boolean;
  optionsChipProps?: ChipProps & {
    labelProps?: TextProps;
    sxWithOption?: (theme?: Theme, option?: ComboBoxOption) => object;
  };
  renderValue?: (value?: Partial<ComboBoxOption>[]) => React.ReactNode;
  api?: ComboBoxApi;
  optionsLimit?: number;
  atSearch?: boolean;
  noOptionsText?: React.ReactNode;
  onChange?: (
    event: React.SyntheticEvent,
    value: Partial<ComboBoxOption>[],
    reason: string,
    changedValues: Partial<ComboBoxOption>[]
  ) => void;
  triggerStartIcon?: (open?: boolean) => React.ReactNode;
  triggerEndIcon?: (open?: boolean) => React.ReactNode;
  disabled?: boolean;
  /** When set, applies minHeight/height to the trigger (e.g. 25 for compact page header) */
  triggerHeight?: number;
  size?: 'sm' | 'md';
  triggerSx?: (open?: boolean) => SxProps<Theme>;
  triggerContentVariant?: Variants;
  menuItemSx?: (option?: ComboBoxOption) => SxProps<Theme>;
  /** Multi-line option rows; `isInvalidOption` marks non-selectable section headers (no warning styling). */
  multilineOptions?: boolean;
  /** When true, menu paper width matches the trigger (anchor) width. Use for page header / full-width layouts. */
  alignMenuWidthToTrigger?: boolean;
  /**
   * When `false` (default), the menu is only mounted while open (`open && <Menu>`), which defers work.
   * When `true`, the menu stays mounted and only `open` toggles visibility—often smoother (less flicker)
   */
  keepMenuMounted?: boolean;
  /**
   * Callout/banner above search and options (e.g. “Missing from the list?” with a support link).
   * Wrapped with default subtle warning background — wrap content or use `sx` on inner elements if needed.
   */
  dropdownMenuBanner?: React.ReactNode;
  enableSearchFullWidth?: boolean;
  /**
   * When `api.search` is true, the query normally runs only after the user types.
   * Set true to load options with an empty search (e.g. on menu open) so the list is populated before typing.
   */
  allowEmptySearchFetch?: boolean;
  triggerClassName?: string;
}

const defaultValueRender = (value?: Partial<ComboBoxOption>[]) => {
  return (
    <AvatarGroup max={5} overlap="small">
      {value?.map(item => <Avatar key={item?.id} size="xsl" letters={item?.label} />)}
    </AvatarGroup>
  );
};

export const CustomDropDown = ({
  options = [],
  value: propsValue,
  placeholder = t`Select an option`,
  searchPlaceholder = t`Search`,
  width = 256,
  menuWidth,
  singleSelect = false,
  enableSearch = true,
  isInvalidOption,
  isDisabledOption,
  renderOption,
  optionsChipProps,
  optionsChipRender,
  renderValue = defaultValueRender,
  api = null,
  optionsLimit,
  atSearch = false,
  noOptionsText = t`No options`,
  onChange,
  triggerEndIcon,
  triggerStartIcon,
  disabled,
  triggerHeight,
  size = 'md',
  triggerSx,
  triggerContentVariant,
  menuItemSx,
  multilineOptions = false,
  alignMenuWidthToTrigger = false,
  keepMenuMounted = false,
  dropdownMenuBanner,
  enableSearchFullWidth = false,
  allowEmptySearchFetch = false,
  triggerClassName,
}: CustomDropDownProps) => {
  const sizeConfig = CUSTOM_DROPDOWN_SIZE_CONFIG[size];
  const {
    listItemHeight,
    fontSize: listItemFontSize,
    optionPadding,
    emptyStatePadding,
  } = sizeConfig;
  const sizeSx = multilineOptions
    ? {
        alignItems: 'flex-start',
        height: 'auto',
        minHeight: listItemHeight,
        ...(listItemFontSize && { fontSize: listItemFontSize }),
      }
    : {
        minHeight: listItemHeight,
        height: listItemHeight,
        ...(listItemFontSize && { fontSize: listItemFontSize }),
      };

  /** Banner + spacing sit inside the same max-height as search + list; without this bump the list scrollbar is clipped. */
  const menuBannerExtraPx = dropdownMenuBanner ? 80 : 0;

  const [search, setSearch] = useState('');
  const [apiSearch, setApiSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const value = propsValue?.map(item => {
    const valueOption = options?.find(i => i.id === item?.id);
    return { ...valueOption, ...item };
  });

  // Helper to resolve vars (can be array or function)
  const getVars = () => {
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  };

  const parsedApiSearch = parseSearch(atSearch, apiSearch);

  const isApiSearchEnabled = (() => {
    if (!api?.search) return !!api;
    if (allowEmptySearchFetch) {
      if (typeof api.vars === 'function' && getVars().length === 0) return false;
      return open || Boolean(parsedApiSearch);
    }
    return Boolean(parsedApiSearch);
  })();

  // API data fetching
  const { data, isLoading } = useGet(
    api?.url || null,
    api?.search ? [api?.key, apiSearch] : api?.key || '',
    api?.search ? [parsedApiSearch, ...getVars()] : [...getVars()],
    undefined,
    api?.revalidate !== false,
    {
      ...(api?.options ?? {}),
      enabled: isApiSearchEnabled,
    }
  );

  const apiSelectOptions = (
    data && api ? api.responseTransform(data) : options
  ) as ComboBoxOption[];

  const selectOptions = useMemo(() => {
    const merged = new Map<ComboBoxOption['id'], ComboBoxOption>();
    apiSelectOptions.forEach(option => merged.set(option.id, option));
    propsValue?.forEach(item => {
      if (item?.id != null && !merged.has(item.id)) {
        merged.set(item.id, { id: item.id, label: String(item?.label ?? '') });
      }
    });
    return Array.from(merged.values());
  }, [apiSelectOptions, propsValue]);

  // Debounced search for API calls
  const inputChangeApi = useCallback(
    debounce((newInputValue: string) => {
      setApiSearch(newInputValue);
    }, 250),
    []
  );

  // Filter options based on search value
  const filteredOptions = (() => {
    const optionsToFilter = selectOptions;

    if (!enableSearch) {
      return optionsToFilter;
    }

    if (api?.search) {
      if (!parsedApiSearch) {
        return allowEmptySearchFetch ? optionsToFilter.slice(0, optionsLimit) : [];
      }
      return optionsToFilter
        .filter(option => option.label.toLowerCase().includes(parsedApiSearch.toLowerCase()))
        .slice(0, optionsLimit);
    }

    // For local search, use the search state (immediate filtering)
    return optionsToFilter.filter(option =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  })();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (enableSearch) {
      setSearch('');
      setApiSearch('');
    }
  };

  const handleOptionClick = (option: ComboBoxOption, event: React.MouseEvent) => {
    if (isDisabledOption?.(option) || isInvalidOption?.(option)) {
      return;
    }

    const currentValue = value || [];
    const isSelected = currentValue.find(item => item?.id === option.id);

    let newValue: Partial<ComboBoxOption>[];
    let reason: string;
    let changedValues: Partial<ComboBoxOption>[];

    if (singleSelect) {
      newValue = isSelected ? [] : [option];
      reason = isSelected ? 'removeOption' : 'selectOption';
      changedValues = [option];
      onChange?.(event, newValue, reason, changedValues);
      setAnchorEl(null);
      return;
    }

    if (isSelected) {
      // Remove the option (deselect)
      newValue = currentValue.filter(item => item?.id !== option.id);
      reason = 'removeOption';
      changedValues = [option]; // Removed values
    } else {
      // Add the option (select)
      newValue = [...currentValue, option];
      reason = 'selectOption';
      changedValues = [option]; // Newly selected values
    }

    // Call onChange with the new value and changed values
    onChange?.(event, newValue, reason, changedValues);
  };

  return (
    <>
      <Button
        className={triggerClassName}
        sx={{
          padding: '9px 14px',
          borderRadius: '8px',
          border: `0.5px solid ${palette.border.primarySubtle}`,
          background: palette.bg.primary,
          '&:hover': {
            background: palette.bg.primary,
            border: `0.5px solid ${palette.border.brand}`,
          },
          justifyContent: 'space-between',
          textTransform: 'none',
          width,
          ...(open
            ? {
                border: `0.5px solid ${palette.border.brand}`,
                background: palette.bg.primary,
                boxShadow: `0 1px 2px 0 rgba(16, 24, 40, 0.05), 0 0 0 4px rgba(22, 105, 99, 0.08)`,
              }
            : {}),
          ...(triggerHeight != null && {
            minHeight: triggerHeight,
            height: triggerHeight,
          }),
          ...triggerSx?.(open),
          '& [data-custom-dropdown-trigger-content]': {
            minWidth: 0,
            overflow: 'hidden',
          },
        }}
        id="custom-dropdown-trigger"
        data-testid="custom_dropdown_trigger"
        data-has-value={value?.length ? '' : undefined}
        aria-controls={open ? 'custom-dropdown-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        size="lg"
        onClick={handleClick}
        variant="tertiaryOutline"
        startIcon={triggerStartIcon?.(open)}
        endIcon={triggerEndIcon?.(open)}
        disabled={disabled}
      >
        <Text
          variant={triggerContentVariant ?? 'bodyMd'}
          color="placeholder"
          noWrap
          data-custom-dropdown-trigger-content
        >
          {value?.length ? renderValue?.(value) : placeholder}
        </Text>
      </Button>
      {(keepMenuMounted || open) && (
        <Menu
          id="custom-dropdown-menu"
          data-testid="custom_dropdown_menu"
          anchorEl={anchorEl}
          slots={{ transition: Fade }}
          slotProps={{
            paper: {
              sx: {
                ...(alignMenuWidthToTrigger && anchorEl
                  ? {
                      width: anchorEl.clientWidth,
                      minWidth: anchorEl.clientWidth,
                      maxWidth: anchorEl.clientWidth,
                    }
                  : (typeof width === 'number' || menuWidth) && {
                      minWidth: menuWidth ?? width,
                      maxWidth: menuWidth ?? width,
                    }),
                maxHeight: `${326 + menuBannerExtraPx}px`,
                marginTop: '4px',
                borderRadius: '8px',
                border: `0.5px solid ${palette.border.primarySubtle}`,
                boxShadow:
                  '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
                ul: {
                  maxHeight: `${316 + menuBannerExtraPx}px`,
                  overflow: 'hidden',
                  li: { marginBlock: 1 },
                },
              },
            },
            list: {
              sx: {
                padding: '6px',
              },
            },
          }}
          open={open}
          onClose={handleClose}
        >
          {dropdownMenuBanner && (
            <DropdownMenuBannerLi $hasSearch={enableSearch}>
              <DropdownMenuBannerInner>{dropdownMenuBanner}</DropdownMenuBannerInner>
            </DropdownMenuBannerLi>
          )}
          {enableSearch && (
            <li style={{ display: 'flex', justifyContent: 'center' }}>
              <DebouncedInput
                data-testid="custom_dropdown_search"
                onKeyDown={e => {
                  e.stopPropagation();
                }}
                sx={{
                  width: enableSearchFullWidth ? '100%' : 'unset',
                  input: { padding: '6px 10px' },
                }}
                placeholder={searchPlaceholder}
                value={search}
                onChange={v => {
                  setSearch(v);
                  if (api?.search) {
                    inputChangeApi(v);
                  }
                }}
              />
            </li>
          )}
          {isLoading && api?.search && (
            <CustomDropDownMenuItem
              $size={size}
              disabled
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: emptyStatePadding,
                ...sizeSx,
              }}
            >
              <Text variant="bodySm" color="placeholder">
                {t`Loading...`}
              </Text>
            </CustomDropDownMenuItem>
          )}
          {!isLoading && filteredOptions.length === 0 && (
            <NoOptionsDownMenuItem
              $size={size}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: emptyStatePadding,
                ...sizeSx,
              }}
              onClick={e => {
                e.preventDefault();
              }}
              disableTouchRipple
            >
              <Text variant="bodySm" color="placeholder">
                {noOptionsText}
              </Text>
            </NoOptionsDownMenuItem>
          )}
          <CustomDropDownMenuItemWrapper>
            {filteredOptions?.map(option => {
              const isSelected = value?.find(item => item?.id === option.id);
              const isDisabled = Boolean(isDisabledOption?.(option));
              const isSectionHeader = Boolean(multilineOptions && isInvalidOption?.(option));
              const isInvalid = Boolean(!multilineOptions && isInvalidOption?.(option));
              const isNonInteractive = isDisabled || isInvalid;
              const textColor = isNonInteractive ? palette.text.disabled : palette.text.secondary;

              return (
                <CustomDropDownMenuItem
                  key={option.id}
                  $size={size}
                  $multiline={multilineOptions}
                  data-testid={`custom_dropdown_option_${option.id}`}
                  onClick={event => handleOptionClick(option, event)}
                  disabled={isNonInteractive || disabled}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    whiteSpace: multilineOptions ? 'normal' : 'nowrap',
                    overflow: multilineOptions ? 'visible' : 'hidden',
                    textOverflow: multilineOptions ? 'unset' : 'ellipsis',
                    flexDirection: 'row',
                    padding: optionPadding,
                    gap: '8px',
                    marginBlock: '1px',
                    borderRadius: '4px',
                    color: `${textColor} !important`,
                    fill: `${textColor} !important`,
                    cursor: isNonInteractive
                      ? 'not-allowed'
                      : isSectionHeader
                        ? 'default'
                        : 'pointer',
                    ...sizeSx,
                    ...(multilineOptions
                      ? { '& > span': { display: 'block', width: '100%', minWidth: 0 } }
                      : {
                          '& span, & div': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...(listItemFontSize && { fontSize: listItemFontSize }),
                          },
                        }),
                    ...(isInvalid ? { '& .MuiAvatar-root': { opacity: 0.5 } } : {}),
                    ...(isSelected
                      ? {
                          background: palette.bg.brandSubtle,
                          ...(!multilineOptions && {
                            fontWeight: 600,
                            '& span, & div': { fontWeight: 600 },
                          }),
                        }
                      : {}),
                    ...menuItemSx?.(option),
                  }}
                >
                  {isInvalid && (
                    <Warning
                      color="inherit"
                      size={24}
                      style={{ minWidth: '24px', minHeight: '24px' }}
                    />
                  )}
                  <span
                    style={
                      multilineOptions
                        ? { display: 'block', width: '100%', minWidth: 0 }
                        : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                    }
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </span>

                  {(optionsChipRender || optionsChipProps?.label) && (
                    <Chip
                      isTooltip={false}
                      size="sm"
                      label={optionsChipRender?.(option)}
                      {...omit(optionsChipProps, ['labelProps'])}
                      sx={themeSX => {
                        return {
                          marginLeft: 'auto',
                          ...optionsChipProps?.sxWithOption?.(themeSX, option),
                        };
                      }}
                    />
                  )}
                  {isSelected && !multilineOptions && (
                    <Check
                      size={20}
                      style={{
                        minWidth: '20px',
                        minHeight: '20px',
                        ...(!(optionsChipRender || optionsChipProps?.label)
                          ? { marginLeft: 'auto' }
                          : {}),
                      }}
                      color={palette.primary.light}
                    />
                  )}
                </CustomDropDownMenuItem>
              );
            })}
          </CustomDropDownMenuItemWrapper>
        </Menu>
      )}
    </>
  );
};

type SizeProp = { $size?: 'sm' | 'md'; $multiline?: boolean };

const menuItemSizeOptions = {
  shouldForwardProp: (propName: string) => propName !== '$size' && propName !== '$multiline',
};

const NoOptionsDownMenuItem = styled(MenuItem, menuItemSizeOptions)<SizeProp>`
  min-height: ${p => CUSTOM_DROPDOWN_SIZE_CONFIG[p.$size ?? 'md'].listItemHeight}px !important;
  height: ${p => CUSTOM_DROPDOWN_SIZE_CONFIG[p.$size ?? 'md'].listItemHeight}px !important;
  cursor: auto;
`;

const CustomDropDownMenuItem = styled(MenuItem, menuItemSizeOptions)<SizeProp>`
  min-height: ${p =>
    p.$multiline
      ? 'auto'
      : `${CUSTOM_DROPDOWN_SIZE_CONFIG[p.$size ?? 'md'].listItemHeight}px`} !important;
  height: ${p =>
    p.$multiline
      ? 'auto'
      : `${CUSTOM_DROPDOWN_SIZE_CONFIG[p.$size ?? 'md'].listItemHeight}px`} !important;
`;

const DropdownMenuBannerLi = styled('li', {
  shouldForwardProp: prop => prop !== '$hasSearch',
})<{ $hasSearch?: boolean }>`
  display: block;
  list-style: none;
  width: 100%;
  margin: 0;
  padding: 0;
  margin-bottom: ${p => (p.$hasSearch ? '4px' : '0')};
`;

const DropdownMenuBannerInner = styled.div`
  width: 100%;
  border-radius: 6px;
  background: ${palette.bg.warningSubtle};
  padding: 8px 10px;
`;

const CustomDropDownMenuItemWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 248px;
`;

export default CustomDropDown;
